import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Truck, Search, Sparkles, ChevronRight } from 'lucide-react';
import { bevApi } from '../lib/bevApi';

const LOGO_URL = 'https://customer-assets-m6fa6gv7.emergentagent.net/job_d4801e57-518e-48e3-b04b-23ec30a7a74e/artifacts/e12nnw5g_New%20Bev%27s%20Furniture%20Logo.png';

const money = (v) => `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const ProductCard = ({ p }) => (
  <Link to={`/furniture/product/${p.id}`} className="bev-card" data-testid={`bev-product-${p.sku}`}>
    <div className="bev-card-img-wrap">
      {p.images && p.images.length > 0 ? (
        <img src={p.images[0]} alt={p.name} className="bev-card-img" />
      ) : (
        <div className="bev-card-placeholder">No image</div>
      )}
      {p.on_sale && p.sale_label && (
        <span className="bev-card-sale">{p.sale_label}</span>
      )}
      {p.status === 'sold_out' && <span className="bev-card-status">Sold Out</span>}
      {p.status === 'coming_soon' && <span className="bev-card-status">Coming Soon</span>}
    </div>
    <div className="bev-card-body">
      <p className="bev-card-cat">{p.subcategory || p.category}</p>
      <h3 className="bev-card-title">{p.name}</h3>
      <div className="bev-card-price-row">
        {p.on_sale && p.original_price && (
          <span className="bev-card-original">{money(p.original_price)}</span>
        )}
        <span className="bev-card-price">{money(p.price)}</span>
      </div>
    </div>
  </Link>
);

const BevsFurniturePage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('All');
  const [search, setSearch] = useState('');

  useEffect(() => {
    bevApi.listProducts().then((data) => {
      setProducts(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const s = new Set(products.map(p => p.category));
    return ['All', ...Array.from(s)];
  }, [products]);

  const filtered = useMemo(() => {
    return products.filter(p => {
      if (category !== 'All' && p.category !== category) return false;
      if (search && !`${p.name} ${p.subcategory || ''} ${p.description}`.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [products, category, search]);

  const featured = products.filter(p => p.featured).slice(0, 3);
  const onSale = products.filter(p => p.on_sale);

  return (
    <div className="bev-page" data-testid="bev-furniture-page">
      {/* HERO */}
      <section className="bev-hero">
        <div className="bev-hero-inner">
          <img src={LOGO_URL} alt="Bev's Furniture" className="bev-hero-logo" data-testid="bev-hero-logo" />
          <p className="bev-hero-tagline">Home starts with beautiful pieces.</p>
          <p className="bev-hero-sub">
            Sofas, sectionals, recliners, bedroom suites, dining sets & more &mdash; hand-picked for St. Louis families.
          </p>
          <div className="bev-shipping-banner" data-testid="bev-shipping-banner">
            <Truck size={18} />
            <span><strong>Delivery to the St. Louis Metro area only</strong> &nbsp;•&nbsp; Flat <strong>$275</strong> per address</span>
          </div>
        </div>
      </section>

      {/* On Sale Strip */}
      {onSale.length > 0 && (
        <section className="bev-strip" data-testid="bev-sale-strip">
          <div className="bev-container">
            <div className="bev-strip-head">
              <Sparkles size={16} />
              <span>ON SALE NOW</span>
              <span className="bev-strip-count">{onSale.length} item{onSale.length !== 1 ? 's' : ''}</span>
            </div>
            <div className="bev-strip-scroll">
              {onSale.map(p => <ProductCard key={p.id} p={p} />)}
            </div>
          </div>
        </section>
      )}

      {/* Featured */}
      {featured.length > 0 && (
        <section className="bev-featured">
          <div className="bev-container">
            <div className="bev-section-head">
              <p className="bev-eyebrow">Featured</p>
              <h2 className="bev-section-title">Handpicked for your home</h2>
            </div>
            <div className="bev-featured-grid">
              {featured.map(p => <ProductCard key={p.id} p={p} />)}
            </div>
          </div>
        </section>
      )}

      {/* Catalog */}
      <section className="bev-catalog">
        <div className="bev-container">
          <div className="bev-section-head">
            <p className="bev-eyebrow">Shop</p>
            <h2 className="bev-section-title">The full collection</h2>
          </div>

          <div className="bev-filters" data-testid="bev-filters">
            <div className="bev-search-wrap">
              <Search size={16} className="bev-search-icon" />
              <input
                type="text"
                placeholder="Search sofas, beds, dining..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="bev-search-input"
                data-testid="bev-search-input"
              />
            </div>
            <div className="bev-cats">
              {categories.map(c => (
                <button
                  key={c}
                  onClick={() => setCategory(c)}
                  className={`bev-cat-btn ${category === c ? 'is-active' : ''}`}
                  data-testid={`bev-cat-${c.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <p className="bev-empty">Loading collection…</p>
          ) : filtered.length === 0 ? (
            <p className="bev-empty">No products match your search.</p>
          ) : (
            <div className="bev-grid" data-testid="bev-grid">
              {filtered.map(p => <ProductCard key={p.id} p={p} />)}
            </div>
          )}
        </div>
      </section>

      {/* Trust footer */}
      <section className="bev-trust">
        <div className="bev-container">
          <div className="bev-trust-grid">
            <div>
              <ChevronRight size={16} />
              <h4>Local Delivery Only</h4>
              <p>We deliver exclusively to the St. Louis Metropolitan area at a flat <strong>$275 per address</strong>.</p>
            </div>
            <div>
              <ChevronRight size={16} />
              <h4>Secure PayPal Checkout</h4>
              <p>Every purchase is processed through PayPal &mdash; use your PayPal balance, card, or bank account.</p>
            </div>
            <div>
              <ChevronRight size={16} />
              <h4>Questions?</h4>
              <p>Email us at <a href="mailto:romero.sumpter@gmail.com">romero.sumpter@gmail.com</a> and we&apos;ll get back to you the same day.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BevsFurniturePage;
